import React, { useState, useEffect, useCallback } from 'react';
import { Form, Item } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { DateBox } from 'devextreme-react/date-box';
import { TagBox } from 'devextreme-react/tag-box';
import { NumberBox } from 'devextreme-react/number-box';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { useMsal } from "@azure/msal-react";
import { getApiToken } from '../../utils/authUtils.js';
import { GENDERS } from '../../shared/constants';
import notify from 'devextreme/ui/notify';
import { Item as ToolbarItem, Toolbar } from 'devextreme-react/toolbar';
import ScrollView from 'devextreme-react/scroll-view';
import { useNavigate } from 'react-router-dom';
import './report1.scss';
import { openPDFOnDevice } from '../../utils/pdfUtils.js';

export default function Page() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [swimstylesData, setSwimStylesData] = useState([]);
  const [ageGroupsData, setAgeGroupsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    swimstyleIds: [],
    agegroups: [],
    startdate: new Date(2000, 0, 1), // January 1st of current year
    enddate: new Date(), // Today
    genders: [0, 1], // Default to male and female
    top: 25 // Default top 10
  });

  const onLeftClick = () => {
    navigate(-1);
  };

  // Load initial data
  useEffect(() => {
    getApiToken(instance, accounts).then(token => {
      // Load swimstyles
      const swimstylesStore = createStore({
        key: 'swimstyleId',
        loadUrl: `${process.env.REACT_APP_serverEndpoint}/Swimstyle/Swimstyles`,
        onBeforeSend: (method, ajaxOptions) => {
          ajaxOptions.headers = {
            'Authorization': "Bearer " + token
          };
        },
      });

      swimstylesStore.load({sort: 'sortIndex', filter: ['sortIndex', '>=', 0]}).then(data => {
        setSwimStylesData(data);
      }).catch(error => {
        console.error('Error loading swimstyles:', error);
      });

      // Generate age groups (typical swimming age groups)
      const ageGroups = [];
      for (let i = 6; i <= 99; i++) {
        ageGroups.push({ id: i, text: `${i} Jahre` });
      }
      setAgeGroupsData(ageGroups);
    });
  }, [instance, accounts]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const generateReport = useCallback(() => {
    if (!formData.swimstyleIds.length || !formData.agegroups.length) {
      notify({
        message: 'Bitte wählen Sie mindestens eine Schwimmart und eine Altersklasse aus.',
        position: { at: 'bottom center', my: 'bottom center' }
      }, 'warning');
      return;
    }

    setIsLoading(true);
    getApiToken(instance, accounts).then(token => {
      // Create the request object matching AgeGroupRankingRequest
      const requestBody = {
        swimstylesIds: formData.swimstyleIds,
        agegroups: formData.agegroups,
        startdate: formData.startdate.toISOString(),
        enddate: formData.enddate.toISOString(),
        genders: formData.genders,
        top: formData.top
      };

      // Call the GetAgeGroupRankingReport API
      fetch(`${process.env.REACT_APP_serverEndpoint}/Report/GetAgeGroupRankingReport`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then(async blob => {
 var filename = `Altersklassenbestenliste_${new Date().toISOString().split('T')[0]}.pdf`;
          await openPDFOnDevice(blob, filename);


       
        
        notify({
          message: 'PDF-Bericht wurde erfolgreich heruntergeladen.',
          position: { at: 'bottom center', my: 'bottom center' }
        }, 'success');
      })
      .catch(error => {
        console.error('Error generating report:', error);
        notify({
          message: 'Fehler beim Generieren des Berichts.',
          position: { at: 'bottom center', my: 'bottom center' }
        }, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    });
  }, [formData, instance, accounts]);

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-report-details'>
        <Toolbar className='toolbar-details theme-dependent'>
          <ToolbarItem location='before'>
            <Button icon='arrowleft' stylingMode='text' onClick={onLeftClick} />
          </ToolbarItem>
          <ToolbarItem location='before' text='Altersklassenbestenlisten' />
          <ToolbarItem location='after'>
            <Button
              text="PDF-Bericht generieren"
              type="success"
              icon="pdffile"
              onClick={generateReport}
              disabled={isLoading}
            />
          </ToolbarItem>
        </Toolbar>

        <div className='panels'>
          <Form formData={formData} labelLocation="top" className='report-form' colCount={3}>
              <Item dataField="swimstyleIds" label={{ text: 'Schwimmarten' }}>
                <TagBox
                  dataSource={swimstylesData}
                  valueExpr="swimstyleId"
                  displayExpr="name"
                  placeholder="Schwimmarten auswählen..."
                  showSelectionControls={true}
                  value={formData.swimstyleIds}
                  onValueChanged={(e) => handleFieldChange('swimstyleIds', e.value)}
                />
              </Item>
              
              <Item dataField="agegroups" label={{ text: 'Altersklassen' }}>
                <TagBox
                  dataSource={ageGroupsData}
                  valueExpr="id"
                  displayExpr="text"
                  placeholder="Altersklassen auswählen..."
                  showSelectionControls={true}
                  value={formData.agegroups}
                  onValueChanged={(e) => handleFieldChange('agegroups', e.value)}
                />
              </Item>

              <Item dataField="genders" label={{ text: 'Geschlechter' }}>
                <TagBox
                  dataSource={GENDERS}
                  valueExpr="id"
                  displayExpr="text"
                  placeholder="Geschlechter auswählen..."
                  showSelectionControls={true}
                  value={formData.genders}
                  onValueChanged={(e) => handleFieldChange('genders', e.value)}
                />
              </Item>

              <Item dataField="startdate" label={{ text: 'Startdatum' }}>
                <DateBox
                  placeholder="Startdatum"
                  value={formData.startdate}
                  onValueChanged={(e) => handleFieldChange('startdate', e.value)}
                />
              </Item>

              <Item dataField="enddate" label={{ text: 'Enddatum' }}>
                <DateBox
                  placeholder="Enddatum"
                  value={formData.enddate}
                  onValueChanged={(e) => handleFieldChange('enddate', e.value)}
                />
              </Item>

              <Item dataField="top" label={{ text: 'Top X Plätze' }}>
                <NumberBox
                  placeholder="Top X Plätze"
                  min={1}
                  max={100}
                  value={formData.top}
                  onValueChanged={(e) => handleFieldChange('top', e.value)}
                />
              </Item>
            </Form>
            {isLoading && (
              <div className='loading-info'>
                <p>Bericht wird generiert...</p>
              </div>
            )}
        </div>
      </div>
    </ScrollView>
  );
}
